<template>
  <v-app>
    <!-- Barra de navegação principal fixa -->
    <v-app-bar app color="white" dark density="compact">
      <img src="/logo.png" alt="Logo" class="navbar-logo mr-3" />
      <v-toolbar-title>{{ $t('home.title') }}</v-toolbar-title>
      <v-spacer></v-spacer>

      <!-- Nearest deadline countdown (toolbar) -->
      <v-chip
        v-if="nearestDeadline"
        :color="deadlineColor(nearestDeadline.level)"
        size="small"
        label
        variant="flat"
        :class="['mr-1', { 'deadline-blink': nearestDeadline.msRemaining < 3600000 }]"
        :title="$t('home.deadlineAlerts.title')"
        @click="goToDeadlineProject(nearestDeadline)"
      >
        <v-icon start size="x-small">mdi-clock-alert-outline</v-icon>
        {{ formatRemaining(nearestDeadline.msRemaining) }}
      </v-chip>

      <!-- Global deadline signal (always present) -->
      <v-menu :close-on-content-click="false">
        <template v-slot:activator="{ props }">
          <v-badge
            :content="deadlineAlerts.length"
            :model-value="deadlineAlerts.length > 0"
            :color="worstAlertLevel === 'critical' ? 'red' : 'orange'"
            offset-x="6"
            offset-y="6"
          >
            <v-btn icon v-bind="props" size="small" variant="text" :title="$t('home.deadlineAlerts.title')">
              <v-icon :color="deadlineAlerts.length ? (worstAlertLevel === 'critical' ? 'red' : 'orange') : 'grey'">
                {{ deadlineAlerts.length ? 'mdi-bell-alert' : 'mdi-bell-outline' }}
              </v-icon>
            </v-btn>
          </v-badge>
        </template>
        <v-card min-width="320" max-width="440">
          <v-list density="compact">
            <v-list-subheader>{{ $t('home.deadlineAlerts.title') }}</v-list-subheader>
            <v-list-item v-if="deadlineAlerts.length === 0">
              <v-list-item-title class="text-grey">{{ $t('home.deadlineAlerts.empty') }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-for="d in deadlineAlerts"
              :key="d.idProject"
              @click="goToDeadlineProject(d)"
            >
              <template v-slot:prepend>
                <v-icon :color="deadlineColor(d.level)" size="small">mdi-clock-alert-outline</v-icon>
              </template>
              <v-list-item-title class="font-weight-medium">
                {{ d.idProject }}<span v-if="d.reference" class="text-grey"> · {{ d.reference }}</span>
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ deadlineStatusDescr(d) }} — <span :class="d.level === 'overdue' ? 'text-red' : ''">{{ formatRemaining(d.msRemaining) }}</span>
              </v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </v-card>
      </v-menu>

      <!-- Test Date Override (test mode only) -->
      <v-menu v-if="isTestModeAvailable" :close-on-content-click="false">
        <template v-slot:activator="{ props }">
          <v-btn
            v-bind="props"
            size="small"
            variant="text"
            :color="testDate ? 'warning' : 'grey'"
            prepend-icon="mdi-calendar-clock"
          >
            {{ testDate || $t('home.testDate.real') }}
          </v-btn>
        </template>
        <v-card min-width="260" class="pa-3">
          <div class="text-caption text-medium-emphasis mb-2">{{ $t('home.testDate.title') }}</div>
          <v-text-field
            type="datetime-local"
            :model-value="testDate"
            @update:model-value="setTestDate"
            density="compact"
            hide-details
          />
          <v-btn
            class="mt-2"
            size="small"
            variant="tonal"
            color="grey"
            block
            :disabled="!testDate"
            @click="setTestDate(null)"
          >
            {{ $t('home.testDate.reset') }}
          </v-btn>
        </v-card>
      </v-menu>

      <!-- User Name -->
      <span class="mr-3">{{ username }}</span>
      
      <!-- Refresh Button (only on dashboard) -->
      <v-btn
        v-if="currentRoute === 'dashboard'"
        @click="refreshDashboard"
        :loading="isRefreshing"
        size="small"
        prepend-icon="mdi-refresh"
        variant="text"
      >
        {{ $t('dashboard.refresh') }}
      </v-btn>
      
      <!-- LocalBackend Status Indicator -->
      <v-tooltip bottom>
        <template v-slot:activator="{ props }">
          <v-icon
            v-bind="props"
            :color="localBackendConnected ? 'success' : 'error'"
            class="mr-2"
          >
            {{ localBackendConnected ? 'mdi-lan-connect' : 'mdi-lan-disconnect' }}
          </v-icon>
        </template>
        <span>{{ localBackendConnected ? $t('home.localBackendConnected') : $t('home.localBackendDisconnected') }}</span>
      </v-tooltip>
      
      <!-- Menu -->
      <v-menu>
        <template v-slot:activator="{ props }">
          <v-btn icon v-bind="props">
            <v-icon>mdi-menu</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item @click="navigateTo('dashboard')">
            <template v-slot:prepend>
              <v-icon>mdi-view-dashboard</v-icon>
            </template>
            <v-list-item-title>{{ $t('nav.dashboard') }}</v-list-item-title>
          </v-list-item>
          
          <v-list-item @click="navigateTo('archive')">
            <template v-slot:prepend>
              <v-icon>mdi-archive</v-icon>
            </template>
            <v-list-item-title>{{ $t('nav.archive') }}</v-list-item-title>
          </v-list-item>
          
          <v-list-item @click="navigateTo('help')">
            <template v-slot:prepend>
              <v-icon>mdi-help-circle</v-icon>
            </template>
            <v-list-item-title>{{ $t('nav.help') }}</v-list-item-title>
          </v-list-item>
          
          <v-list-item @click="navigateTo('adm-config')">
            <template v-slot:prepend>
              <v-icon>mdi-cog</v-icon>
            </template>
            <v-list-item-title>{{ $t('nav.config') }}</v-list-item-title>
          </v-list-item>

          <v-list-item v-if="userRole === 'superadmin'" @click="navigateTo('users')">
            <template v-slot:prepend>
              <v-icon>mdi-account-group</v-icon>
            </template>
            <v-list-item-title>{{ $t('nav.users') }}</v-list-item-title>
          </v-list-item>
          
          <v-divider></v-divider>
          
          <v-list-item @click="logout">
            <template v-slot:prepend>
              <v-icon>mdi-logout</v-icon>
            </template>
            <v-list-item-title>{{ $t('nav.logout') }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <!-- Conteúdo principal -->
    <v-main>
      <!-- Configuration Error Alert -->
      <v-alert
        v-if="localBackendStore.configurationError"
        type="error"
        variant="tonal"
        closable
        class="ma-4"
      >
        <v-alert-title>{{ $t('home.configurationError') }}</v-alert-title>
        <div>{{ localBackendStore.configurationError }}</div>
        <div v-if="localBackendStore.missingPaths.length > 0" class="mt-2">
          <strong>{{ $t('home.missingPaths') }}:</strong>
          <ul>
            <li v-for="(path, index) in localBackendStore.missingPaths" :key="index">
              {{ path.name }}: {{ path.reason }}
              <span v-if="path.path" class="text-grey">({{ path.path }})</span>
            </li>
          </ul>
        </div>
        <v-btn
          color="primary"
          variant="text"
          @click="navigateTo('adm-config')"
          class="mt-2"
        >
          {{ $t('home.goToConfiguration') }}
        </v-btn>
      </v-alert>
      
      <!-- Configuration Warnings -->
      <v-alert
        v-if="localBackendStore.warnings.length > 0"
        type="warning"
        variant="tonal"
        closable
        class="ma-4"
      >
        <v-alert-title>{{ $t('home.configurationWarnings') }}</v-alert-title>
        <ul>
          <li v-for="(warning, index) in localBackendStore.warnings" :key="index">
            {{ warning }}
          </li>
        </ul>
      </v-alert>
      
      <router-view />
    </v-main>
  </v-app>
</template>

<script setup>
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth.js'
import { useLocalBackendStore } from '../stores/localBackend.js'
import { useProjectStore } from '../stores/projects.js'
import { useDeadlines } from '../composables/useDeadlines.js'
import { computed, onMounted, onUnmounted, watch, ref } from 'vue'
import { testDate, setTestDate, isTestModeAvailable } from '../utils/dateUtils.js'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const localBackendStore = useLocalBackendStore()
const projectStore = useProjectStore()
const { deadlineAlerts, worstAlertLevel, deadlineColor, formatRemaining } = useDeadlines()
const isRefreshing = ref(false)

// Scadenza più vicina (deadlineAlerts è già ordinata per urgenza)
const nearestDeadline = computed(() => deadlineAlerts.value[0] || null)

// Descrizione status per una voce di alert
const deadlineStatusDescr = (alert) => {
  const info = projectStore.getStatusInfo(String(alert.activeStatus), alert.projectType)
  return info?.descr || ''
}

// Apre il progetto dalla lista deadline (porta alla dashboard)
const goToDeadlineProject = (alert) => {
  router.push({ name: 'dashboard', query: { open: alert.idProject } })
}

const currentRoute = computed(() => route.name)
const localBackendConnected = computed(() => localBackendStore.isConnected)

// Estrai il nome utente dal token JWT
const username = computed(() => {
  const decoded = authStore.decodeToken()
  if (decoded) {
    // Il token JWT potrebbe contenere 'sub', 'username', 'name', etc.
    return decoded.sub || decoded.username || decoded.name || 'User'
  }
  return authStore.user || 'User'
})

const userRole = computed(() => authStore.user?.role)

const navigateTo = (routeName) => {
  router.push({ name: routeName })
}

const logout = async () => {
  authStore.logout()
  router.push('/login')
}

// Funzione per refresh della dashboard
const refreshDashboard = () => {
  // Emette un evento che la dashboard può ascoltare
  window.dispatchEvent(new CustomEvent('dashboard-refresh'))
}

// Refresh periodico del segnale deadline (nuove deadline create da altri utenti)
let deadlineRefreshInterval = null

// Check connection on mount and when navigating to dashboard
onMounted(() => {
  localBackendStore.checkConnection()
  // Segnale deadline globale: disponibile da subito, su qualunque schermata
  projectStore.loadDeadlines()
  deadlineRefreshInterval = setInterval(() => projectStore.loadDeadlines(), 5 * 60 * 1000)
})

onUnmounted(() => {
  if (deadlineRefreshInterval) clearInterval(deadlineRefreshInterval)
})

// Check connection when route changes to dashboard
watch(() => route.name, (newRoute) => {
  if (newRoute === 'dashboard') {
    localBackendStore.checkConnection()
  }
})
</script>

<style scoped>
.v-btn--active {
  background-color: rgba(255, 255, 255, 0.2) !important;
}

.navbar-logo {
  max-height: 60px;
  width: auto;
}

/* Lampeggio del countdown quando manca meno di un'ora */
.deadline-blink {
  animation: deadline-blink 1s steps(1, end) infinite;
}

@keyframes deadline-blink {
  50% {
    opacity: 0.25;
  }
}
</style>